import { FC, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getSignInLink } from 'app/store/Auth/selectors';
import { ChakraAuthContentWrapper, PasswordInput } from 'app/components/Core';
import { signInEffect } from 'app/store/Auth/effects';
import { routes } from 'app/routing/routesConfig';
import { parse, stringify } from 'query-string';
import { isWebClient } from 'app/utils/helpers';
import { useForm } from 'react-hook-form';
import { useFetchValidationSchema, useIsMobile } from 'app/utils/hooks';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import { dynamicYupResolver } from 'app/utils/utils';
import { TEST_ID_LOADING_INDICATOR } from 'app/utils/test/constants';
import { ReactComponent as BrandLogo } from 'resources/images/full-logo.svg';
import { ReactComponent as BgPattern } from 'resources/images/Hype_02.svg';
import {
  Box,
  Button,
  Center,
  Checkbox,
  CircularProgress,
  HStack,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ChakraLink } from 'app/components/Core/ChakraLink/Link';
import { InputField } from 'app/components/Core/ChakraInputField/InputField';
import { clickCreateAccountAction } from 'app/store/Mixpanel/slice';
import { bgPatternSx, getStyles } from './styles';
import { defaultValues, errorMessages } from './constants';

interface SignInForm {
  email: string;
  password: string;
  rememberMe: boolean;
}

export const SignIn: FC = () => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile(false);
  const { search, state } = useLocation();
  const navigate = useNavigate();
  const inviteToken = parse(search, { decode: false })?.inviteToken as string;

  // Form validation
  const signInLink = useAppSelector(getSignInLink);
  const { validationSchema } = useFetchValidationSchema({ link: signInLink });

  const yupValidationSchema = useMemo(
    () =>
      validationSchema &&
      transformSchemaToYup(validationSchema, defaultValues, errorMessages),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationSchema, errorMessages]
  );

  const {
    handleSubmit,
    register,
    setError,
    clearErrors,
    formState: { isValid, isDirty, isSubmitting, errors },
  } = useForm<SignInForm>({
    defaultValues,
    mode: 'onChange',
    context: {
      yupValidationSchema,
    },
    resolver: dynamicYupResolver,
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const data = inviteToken ? { ...values, inviteToken } : values;
      await dispatch(signInEffect(data)).unwrap();
      navigate(
        {
          pathname: routes.auth.path,
          search: stringify({ inviteToken }),
        },
        { state }
      );
    } catch (e: any) {
      setError('password', { message: e, type: 'manual' });
      setError('email', { message: '', type: 'manual' });
    }
  });

  const submitDisabled =
    !isDirty ||
    isSubmitting ||
    (!isValid && errors.password?.type !== 'manual');

  const styles = getStyles({ isMobile });

  return (
    <ChakraAuthContentWrapper
      sx={{ 'svg:focus': { outline: 'none' } }}
      centeredContainerProps={{ sx: styles.centeredContainerSx }}
    >
      {!isMobile && <BgPattern style={bgPatternSx} />}
      <Box sx={styles.container}>
        {!isMobile && (
          <Box mb={12} textAlign="center">
            <Icon as={BrandLogo} data-testid="brand-logo" sx={styles.logo} />
          </Box>
        )}

        <VStack
          as="form"
          noValidate
          onSubmit={onSubmit}
          sx={styles.form}
          spacing={0}
        >
          <InputField
            label="Email"
            type="email"
            isInvalid={Boolean(errors?.email)}
            errorMessage={errors?.email?.message}
            {...register('email', {
              onBlur: () => {
                if (errors.password?.type === 'manual') {
                  clearErrors();
                }
              },
            })}
            data-testid="email"
          />
          <Box w="full">
            <PasswordInput
              label="Password"
              isInvalid={Boolean(errors?.password)}
              errorMessage={errors?.password?.message}
              {...register('password', {
                onBlur: () => {
                  if (errors.password?.type === 'manual') {
                    clearErrors();
                  }
                },
              })}
            />
          </Box>
          <ChakraLink
            to={routes.passwordReset.path}
            color="M.70"
            layerStyle="base2"
            fontSize="xs"
            textAlign="left"
            textDecoration="none"
          >
            Forgot your password?
          </ChakraLink>
          <VStack spacing={10} pt={10} w="full">
            {isSubmitting && (
              <Center position="absolute" h={0}>
                <CircularProgress
                  zIndex={1}
                  isIndeterminate
                  color="B.0"
                  data-testid={TEST_ID_LOADING_INDICATOR}
                />
              </Center>
            )}
            <HStack
              w="full"
              mt="0 !important"
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
              gap={4}
            >
              {isWebClient && (
                <Checkbox
                  {...register('rememberMe')}
                  w="full"
                  ml={{ base: 0, md: 0 }}
                  mb={{ base: 4, md: 0 }}
                  {...({ 'data-testid': 'remember-me-checkmark' } as any)}
                  className="hasBorder"
                >
                  Remember me
                </Checkbox>
              )}
              <Button
                type="submit"
                w="full"
                isDisabled={submitDisabled}
                data-testid="signin-btn"
                marginInlineStart="0px !important"
              >
                Sign In
              </Button>
            </HStack>

            <VStack borderTop="1px solid" borderColor="M.30" w="full" mx="auto">
              <HStack layerStyle="base2" pt={8}>
                <Text>Don&apos;t have an account?</Text>
                <ChakraLink
                  to={{
                    pathname: routes.signUp.path,
                    search,
                  }}
                  fontWeight={600}
                  onClick={() => dispatch(clickCreateAccountAction())}
                >
                  Create Account
                </ChakraLink>
              </HStack>
            </VStack>
          </VStack>
        </VStack>
      </Box>
    </ChakraAuthContentWrapper>
  );
};
